/** Explicit, public interests only. These matches never consume inferred emotional data. */
export function interests(value) {
  return [
    ...new Set(
      String(value ?? '')
        .split(/[·,]/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    ),
  ].slice(0, 12);
}
export function sharedInterests(a, b) {
  const right = new Set(interests(b));
  return interests(a).filter((v) => right.has(v));
}
export function hashtags(text) {
  return [
    ...new Set(
      [...String(text).matchAll(/(?:^|\s)#([\p{L}\p{N}_-]{1,40})/gu)].map((m) =>
        m[1].toLowerCase(),
      ),
    ),
  ].slice(0, 10);
}
/** Project only explicit authored tags, including legacy manual visual labels.
 * This does not classify text or infer emotional/psychological attributes. */
export function postHashtags(post) {
  const authored = [
    ...(Array.isArray(post.hashtags) ? post.hashtags : []),
    ...(Array.isArray(post.visualMetadata?.tags) ? post.visualMetadata.tags : []),
    ...hashtags(post.text),
  ];
  return [
    ...new Set(
      authored
        .filter((tag) => typeof tag === 'string')
        .map((tag) => tag.trim().toLowerCase().replaceAll(' ', '-'))
        .filter((tag) => /^[\p{L}\p{N}_-]{1,40}$/u.test(tag)),
    ),
  ].slice(0, 10);
}
export const LIFETIMES = Object.freeze({
  permanent: null,
  '1h': 3600000,
  '24h': 86400000,
  '7d': 604800000,
});
/** Stable partition keeps unseen relevant items before the earlier-viewed boundary. */
export function readingOrder(ranked, seen) {
  const ids = new Set(seen);
  return [...ranked.filter((p) => !ids.has(p.id)), ...ranked.filter((p) => ids.has(p.id))].map(
    (p) => ({ ...p, seen: ids.has(p.id) }),
  );
}

export const CONNECTION_LABELS = Object.freeze([
  'friend',
  'family',
  'brother',
  'sister',
  'sibling',
  'parent',
  'child',
  'partner',
]);
export const SOCIAL_AUDIENCES = Object.freeze([
  'public',
  'friends-of-friends',
  'friends',
  'private',
]);
export const SOCIAL_PRIVACY_DEFAULTS = Object.freeze({
  discoverability: 'public',
  connections: 'friends',
  requests: 'public',
});

/** Resolve persisted privacy conservatively. Missing legacy fields retain migration defaults;
 * malformed stored values fail closed instead of widening access. */
export function socialPrivacy(settings) {
  const stored = settings?.socialPrivacy;
  if (stored !== undefined && (!stored || typeof stored !== 'object' || Array.isArray(stored)))
    return Object.fromEntries(Object.keys(SOCIAL_PRIVACY_DEFAULTS).map((key) => [key, 'private']));
  return Object.fromEntries(
    Object.entries(SOCIAL_PRIVACY_DEFAULTS).map(([key, fallback]) => [
      key,
      settings?.socialPrivacy?.[key] === undefined
        ? fallback
        : SOCIAL_AUDIENCES.includes(settings.socialPrivacy[key])
          ? settings.socialPrivacy[key]
          : 'private',
    ]),
  );
}

/** Validate a desired privacy patch. Unknown fields/values throw before persistence. */
export function validateSocialPrivacy(value) {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    !Object.keys(value).length ||
    Object.entries(value).some(
      ([key, audience]) =>
        !Object.hasOwn(SOCIAL_PRIVACY_DEFAULTS, key) || !SOCIAL_AUDIENCES.includes(audience),
    )
  )
    throw new TypeError('Choose a valid connection privacy setting.');
  return { ...value };
}

/** Build one request-owned accepted graph in O(users + edges). Blocks remove both directions.
 * No cache or UI state grants membership; callers rebuild after every persisted mutation. */
export function socialGraph(state) {
  const adjacency = new Map(state.users.map((user) => [user.id, new Set()]));
  const blocked = (a, b) =>
    !!(state.settings[a]?.blocked.includes(b) || state.settings[b]?.blocked.includes(a));
  for (const [a, b] of state.friendships) {
    if (a === b || !adjacency.has(a) || !adjacency.has(b) || blocked(a, b)) continue;
    adjacency.get(a).add(b);
    adjacency.get(b).add(a);
  }
  const friends = (a, b) => adjacency.get(a)?.has(b) === true;
  const mutual = (a, b) => [...(adjacency.get(a) ?? [])].filter((id) => friends(b, id));
  const allows = (owner, viewer, audience) =>
    adjacency.has(owner) &&
    adjacency.has(viewer) &&
    !blocked(owner, viewer) &&
    (owner === viewer ||
      audience === 'public' ||
      (['friends', 'friends-of-friends'].includes(audience) && friends(owner, viewer)) ||
      (audience === 'friends-of-friends' && mutual(owner, viewer).length > 0));
  const permits = (owner, viewer, key) =>
    allows(owner, viewer, socialPrivacy(state.settings[owner])[key]);
  // An edge is disclosed only when BOTH endpoints permit their connection list.
  const edgeVisible = (a, b, viewer) =>
    friends(a, b) &&
    (viewer === a ||
      viewer === b ||
      (permits(a, viewer, 'connections') && permits(b, viewer, 'connections')));
  return { adjacency, blocked, friends, mutual, allows, permits, edgeVisible };
}

/** Family is an owner's private organization of accepted friends, never a public kinship claim.
 * Missing/stale labels and removed/blocked friendships grant no audience access. */
export function inFamily(state, owner, viewer, graph = socialGraph(state)) {
  const label = state.settings[owner]?.connectionLabels?.[viewer];
  return graph.friends(owner, viewer) && isFamilyLabel(label);
}

/** Recognize explicit private family labels; unknown persisted values never grant access. */
export function isFamilyLabel(label) {
  return CONNECTION_LABELS.includes(label) && label !== 'friend';
}
